// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Rocket, Loader2, ChevronRight, X, Check, Trash2, AlertTriangle,
  Search, Bot, FileText, ChevronDown,
} from 'lucide-react';
import {
  Action, Chip, Divided, EmptyState, FilterChips, ListRow, Loading, PageBody, PageHead, Panel, RouteMark, StatusChip,
  SearchField,
} from '../../components/aa';
import { agentsApi } from '../../services/agentsApi';
import { getBuilderStatus, requestAgentBridgeUrl, getProviderExperts, assignExpertToProject, getProjectsRoster } from '../../services/api';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { fetchState } from '../../orbit/lib/intakeApi';
import type { Project } from '../../types/agents';
import { useParticipant } from '../../hooks/useParticipant';

const STATUS_MAP: Record<string, { label: string; dotClass: string; badgeClass: string; pulse?: boolean }> = {
  PENDING: { label: 'Queued', dotClass: 'bg-gray-400', badgeClass: 'bg-gray-50 text-gray-600 ring-gray-200' },
  REQUESTED: { label: 'Queued', dotClass: 'bg-gray-400', badgeClass: 'bg-gray-50 text-gray-600 ring-gray-200' },
  IN_PROGRESS: { label: 'Working', dotClass: 'bg-brand-500', badgeClass: 'bg-brand-50 text-brand-600 ring-brand-200', pulse: true },
  IN_DEVELOPMENT: { label: 'In Development', dotClass: 'bg-brand-500', badgeClass: 'bg-brand-50 text-brand-600 ring-brand-200', pulse: true },
  TESTING: { label: 'Testing', dotClass: 'bg-amber-400', badgeClass: 'bg-amber-50 text-amber-700 ring-amber-200', pulse: true },
  COMPLETED: { label: 'Complete', dotClass: 'bg-gray-900', badgeClass: 'bg-gray-100 text-gray-900 ring-gray-300' },
  LIVE: { label: 'Live', dotClass: 'bg-brand-500', badgeClass: 'bg-brand-50 text-brand-700 ring-brand-200' },
  FAILED: { label: 'Investigating', dotClass: 'bg-red-500', badgeClass: 'bg-red-50 text-red-700 ring-red-200' },
  CANCELLED: { label: 'Cancelled', dotClass: 'bg-gray-300', badgeClass: 'bg-gray-50 text-gray-500 ring-gray-200' },
};

function getStatusConfig(status: string) {
  return STATUS_MAP[status] || STATUS_MAP.PENDING;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatDomain(domain?: string | null): { label: string; color: string } {
  if (!domain) return { label: '—', color: 'text-gray-400' };
  const map: Record<string, { label: string; color: string }> = {
    CODE: { label: 'Code', color: 'text-brand-500' },
    LEGAL: { label: 'Legal', color: 'text-amber-600' },
    MEDICAL: { label: 'Medical', color: 'text-red-600' },
    BUSINESS: { label: 'Business', color: 'text-brand-500' },
    CREATIVE: { label: 'Creative', color: 'text-gray-600' },
    TECHNICAL: { label: 'Technical', color: 'text-cyan-600' },
  };
  return map[domain] || { label: domain.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), color: 'text-gray-600' };
}

// Presentation view-model for a build row. The customer-facing LABEL is
// validx's own `latestDisplay.status` (the source of truth — it already says
// "Live" / "Queued" / "Building" etc.), falling back to the raw-status map.
// We never invent a label or a state the data doesn't carry: a PENDING /
// REQUESTED validx project is a build QUEUED with a ready PRD — its workspace
// exists and is openable, so it is not "drafting".
type BuildView = {
  mode: 'live' | 'building' | 'queued' | 'attention' | 'failed' | 'cancelled';
  status: string;                  // human label (validx truth)
  dot: string;                     // status-dot hex
  tile: string;                    // icon-tile classes
  Icon: React.ComponentType<any>;
  spin: boolean;
  openable: boolean;               // is there a workspace to open?
};

function describeBuild(v: Project): BuildView {
  const cat = v.latestDisplay?.category;
  const status = (v.status || '').toUpperCase();
  const label = cat === 'attention'
    ? 'Needs input'
    : (v.latestDisplay?.status || getStatusConfig(status).label);
  if (cat === 'attention') {
    return { mode: 'attention', status: 'Needs input', dot: '#f59e0b', tile: 'bg-amber-50 text-amber-600', Icon: AlertTriangle, spin: false, openable: true };
  }
  if (status === 'FAILED') {
    return { mode: 'failed', status: label || 'Investigating', dot: '#ef4444', tile: 'bg-red-50 text-red-600', Icon: AlertTriangle, spin: false, openable: true };
  }
  if (status === 'CANCELLED') {
    return { mode: 'cancelled', status: 'Cancelled', dot: '#9ca3af', tile: 'bg-gray-100 text-gray-400', Icon: X, spin: false, openable: false };
  }
  if (status === 'LIVE' || status === 'COMPLETED' || cat === 'done') {
    return { mode: 'live', status: label, dot: '#16a34a', tile: 'bg-gray-900 text-white', Icon: Bot, spin: false, openable: true };
  }
  if (['IN_PROGRESS', 'IN_DEVELOPMENT', 'TESTING'].includes(status) || cat === 'working' || cat === 'milestone') {
    return { mode: 'building', status: label, dot: '#18191d', tile: 'bg-brand-50 text-brand-600', Icon: Loader2, spin: true, openable: true };
  }
  // PENDING / REQUESTED / setup → build is queued; the PRD is drafted and the
  // workspace exists. Show validx's label ("Queued") and let the user open it.
  return { mode: 'queued', status: label || 'Queued', dot: '#9ca3af', tile: 'bg-gray-100 text-gray-500', Icon: FileText, spin: false, openable: true };
}

/* A build's state, said in the lifecycle vocabulary the rest of the portal
   uses. `describeBuild` still decides WHICH state it is and what to call it;
   this only says which of the shared tones that state belongs to, so a queued
   build and a pending request read the same in the tab beside it. */
const BUILD_STATUS: Record<string, string> = {
  attention: 'needs_review',
  failed: 'blocked',
  cancelled: 'retired',
  live: 'active',
  building: 'in_progress',
  queued: 'pending',
};
const buildStatus = (mode: string): string => BUILD_STATUS[mode] || 'pending';

const ROSTER_STATUS: Record<string, string> = {
  COMPLETED: 'active',
  LIVE: 'active',
  IN_PROGRESS: 'in_progress',
  ASSIGNED: 'in_progress',
  PENDING: 'pending',
};
const rosterStatus = (s?: string): string => ROSTER_STATUS[(s || '').toUpperCase()] || 'pending';

/* Who from the serving provider is on this build. Said only when there is
   someone: "no expert yet" is already the answer the Assign control gives. */
const expertLine = (v: any): React.ReactNode => {
  if (!v.expert?.name) return undefined;
  const provider = localStorage.getItem('serving_provider_name');
  let from = provider || 'Agents Anywhere';
  if (!provider && localStorage.getItem('is_provider') === '1') from = 'your team';
  let line = `${v.expert.name} from ${from} is your expert`;
  if (v.expert.department) line = `${line} · ${v.expert.department}`;
  return line;
};

/** Searchable client picker (CTO 08-18): chips die past ten clients — one
 *  pill that opens a search-first dropdown scales to any roster.
 *
 *  The menu is PORTALLED to the body rather than positioned inside the pill.
 *  It used to be an absolutely positioned child, which a Panel, every one of
 *  which clips its own corners with overflow-hidden, cut off at the panel's
 *  edge: the roster ran past the bottom of the card and the last client was
 *  sliced in half. Nothing can be scrolled into view that is outside the box
 *  doing the clipping, so the fix is to leave the box. Fixed coordinates are
 *  measured off the pill and refreshed while the menu is open, because a
 *  portalled menu does not travel with the page the way a child would. */
const ClientPicker: React.FC<{
  clients: Array<[string, number]>;
  value: string;
  onChange: (v: string) => void;
  total: number;
}> = ({ clients, value, onChange, total }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const MENU_W = 288;

  const place = useCallback(() => {
    const pill = ref.current?.getBoundingClientRect();
    if (!pill) return;
    // Kept on screen at both edges: flush left would run off a narrow viewport,
    // and a pill near the right edge would push the menu past it.
    const left = Math.max(8, Math.min(pill.left, window.innerWidth - MENU_W - 8));
    setAt({ top: pill.bottom + 8, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    // Capture phase: the pill sits inside scrollable panels, not just the page.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const matches = clients.filter(([name]) => !q.trim() || name.toLowerCase().includes(q.trim().toLowerCase()));
  const pick = (v: string) => { onChange(v); setOpen(false); setQ(''); };

  const menu = (
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: at?.top ?? 0, left: at?.left ?? 0, width: MENU_W }}
      className="z-[60] rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
        <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Search clients…"
          className="w-full bg-transparent border-0 outline-none text-[13px] text-gray-900 placeholder:text-gray-400" />
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        <button type="button" onClick={() => pick('all')}
          className={`w-full flex items-center justify-between px-3.5 py-2 text-[13px] text-left transition-colors ${value === 'all' ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}>
          All clients <span className="text-[11px] text-gray-400 tabular-nums">{total}</span>
        </button>
        {matches.map(([name, count]) => (
          <button key={name} type="button" onClick={() => pick(name)}
            className={`w-full flex items-center justify-between px-3.5 py-2 text-[13px] text-left transition-colors ${value === name ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}>
            <span className="truncate">{name}</span>
            <span className="text-[11px] text-gray-400 tabular-nums shrink-0 ml-2">{count}</span>
          </button>
        ))}
        {matches.length === 0 && <div className="px-3.5 py-3 text-[12.5px] text-gray-400">No client matches "{q}".</div>}
      </div>
    </div>
  );

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(v => !v)}
        className={`whitespace-nowrap inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12px] font-medium transition-colors ${value !== 'all' ? 'bg-black text-white' : 'bg-brand-50 text-gray-700 hover:text-gray-900'}`}>
        {value === 'all' ? `All clients · ${total}` : value}
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? '-rotate-90' : 'rotate-90'} ${value !== 'all' ? 'text-white/70' : 'text-gray-400'}`} />
      </button>
      {open && at && createPortal(menu, document.body)}
    </div>
  );
};

// One column plan for every own-company row, scoping or build, so the
// Answered/Updated columns and the action cluster line up down the list.
const OWN_COLUMNS = [100, 110];
const OWN_RIGHT = 420;

const shortName = (who: string): string => (who.includes('@') ? who.split('@')[0] : who);

const AssignEngineer: React.FC<{
  current?: string | null;
  experts: Array<{ email: string; name: string }>;
  onPick: (value: string) => void;
}> = ({ current, experts, onPick }) => (
  <span className="relative inline-flex">
    <select
      defaultValue=""
      aria-label="Assign an expert"
      title={current ? `Assigned to ${current}` : 'Assign an expert'}
      onChange={(e) => { const value = e.target.value; e.target.value = ''; onPick(value); }}
      className="inline-flex h-[28px] w-[176px] cursor-pointer appearance-none items-center truncate rounded-act border border-line bg-white pl-2.5 pr-7 text-[12px] font-semibold leading-none text-ink transition-colors hover:bg-canvas-inset focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ink/15"
    >
      <option value="">{current ? `Expert: ${shortName(current)}` : 'Assign expert'}</option>
      <optgroup label="Dedicated expert">
        {experts.map((x) => (
          <option key={`fde-${x.email}`} value={`FDE|${x.email}`}>{shortName(x.name)}</option>
        ))}
      </optgroup>
      <optgroup label="Support when blocked">
        {experts.map((x) => (
          <option key={`support-${x.email}`} value={`BLOCKER_SUPPORT|${x.email}`}>{shortName(x.name)}</option>
        ))}
      </optgroup>
      {experts.length === 0 && <option value="" disabled>No experts yet. Switch on Builder expert in People &amp; departments.</option>}
    </select>
    <ChevronDown size={13} aria-hidden className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-soft" />
  </span>
);

/* `lock="client"` shows client work only, without the Ours | Client switch;
   `onlyClientId` narrows it to one client, for that client's own page. */
export const CustomerProjectsView: React.FC<{
  providerScope?: 'owner' | 'expert';
  lock?: 'client';
  onlyClientId?: string;
}> = ({ providerScope, lock, onlyClientId }) => {
  const navigate = useNavigate();
  const clientId = localStorage.getItem('client_id');
  const { isImpersonating } = useImpersonation();
  // Provider lens: one Ours | Client toggle. Default OURS. Experts are fixed to Client.
  const [scope, setScope] = useState<'ours' | 'client'>(providerScope === 'expert' || lock === 'client' ? 'client' : 'ours');
  const [roster, setRoster] = useState<any>(null);
  const [rosterLoading, setRosterLoading] = useState(!!providerScope);
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [openingId, setOpeningId] = useState<string | null>(null);
  useEffect(() => {
    if (!providerScope) return;
    let alive = true;
    (async () => {
      try { const r = await getProjectsRoster(); if (alive) setRoster(r); }
      catch { if (alive) setRoster({ available: false }); }
      finally { if (alive) setRosterLoading(false); }
    })();
    return () => { alive = false; };
  }, [providerScope]);
  // Who may assign an engineer/expert to a project: super, the provider's
  // Owner, or anyone acting-as (impersonation is staff-initiated). The same
  // line the server draws; a plain provider member's pick was refused there.
  const canAssignExpert =
    localStorage.getItem('user_role') === 'super' ||
    localStorage.getItem('is_provider_owner') === '1' ||
    isImpersonating;
  const [experts, setExperts] = useState<Array<{ email: string; name: string; role: string }>>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [validxAppUrl, setValidxAppUrl] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [bq, setBq] = useState('');

  useEffect(() => {
    getBuilderStatus()
      .then(data => { if (data.validxAppUrl) setValidxAppUrl(data.validxAppUrl); })
      .catch(() => {});
  }, []);

  // Load the provider's assignable people for the per-project assign control.
  useEffect(() => {
    if (canAssignExpert) {
      getProviderExperts().then(d => setExperts(d?.experts || [])).catch(() => { });
    }
  }, [canAssignExpert]);

  const fetchAgents = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const res = await agentsApi.listProjects({ clientId });
      const data = res.data;

      let agents: Project[] = [];
      if (data?.content) {
        for (const part of data.content) {
          if (part.text) {
            try {
              const parsed = JSON.parse(part.text);
              if (Array.isArray(parsed)) agents = parsed;
              else if (parsed.projects) agents = parsed.projects;
            } catch {}
          }
        }
      } else if (Array.isArray(data)) {
        agents = data;
      }

      setProjects(agents);
    } catch (err) {
      console.error('Error fetching agents:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleRequestAgent = async () => {
    if (!clientId) return;

    // "Request New Agent" means start fresh — go to L1 intake. We do
    // NOT hijack the user back to their current engagement's
    // route_plan.next_best_action; that's MyBusiness's job (the home
    // surface for the engagement in flight). The Agents page lists
    // every project and its "new" button is for explicitly new builds.
    //
    // Power-user fast path: if validx exposes a builder bridge URL
    // (skips L1, lands directly in a fresh validx project for users
    // who already know what they want), open that. Otherwise route
    // through Orbit intake.
    if (validxAppUrl) {
      try {
        const data = await requestAgentBridgeUrl(
          `/customer/project/new/builder?clientId=${clientId}`,
          clientId || undefined,
        );
        if (data.url) {
          window.open(data.url, '_blank', 'noopener,noreferrer');
          return;
        }
      } catch (err) {
        console.warn('[agents] bridge URL unavailable, routing through Orbit intake', err);
      }
    }

    // Fallback — Orbit intake handles cold-start + extends to multiple
    // engagements per client once the schema supports it.
    navigate('/orbit/intake');
  };

  if (!clientId) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-gray-500">No client context available.</p>
      </div>
    );
  }

  // Only builds live here. Problems still being scoped are in the Clarity tab.
  const filtered = projects.filter(p => !bq || (p.title || '').toLowerCase().includes(bq.toLowerCase()));

  const openBuild = async (v: Project) => {
    // The workspace lives on validx — always open via the HMAC bridge. The
    // in-app /agents/[id] viewer was retired.
    if (validxAppUrl && clientId) {
      try {
        const data = await requestAgentBridgeUrl(
          `/customer/project/${v.id}/builder/?clientId=${clientId}`,
          clientId || undefined,
        );
        if (data.url) { window.open(data.url, '_blank', 'noopener,noreferrer'); return; }
      } catch { /* fall through to the error toast below */ }
    }
    toast.error('Could not open the builder. Please try again.');
  };

  const assignExpert = async (projectId: string, value: string) => {
    const [serviceModel, email] = value.split('|', 2) as ['BLOCKER_SUPPORT' | 'FDE', string];
    if (!email) return;
    try {
      await assignExpertToProject(projectId, email, serviceModel);
      toast.success(serviceModel === 'FDE' ? 'Dedicated expert assigned' : 'Expert assigned for support');
      fetchAgents();
      if (providerScope) getProjectsRoster().then(setRoster).catch(() => undefined);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not assign the expert');
    }
  };

  // ── Provider "Client" scope: roster rows as cards. ─────────────────────────
  const showClientWork = !!providerScope && (providerScope === 'expert' || scope === 'client');
  const ofThisClient = (id?: string) => !onlyClientId || id === onlyClientId;
  const clientWorkAll: any[] = (providerScope === 'owner' ? (roster?.client_work || []) : (roster?.projects || []))
    .filter((r: any) => ofThisClient(r.client_id || r.workspace_id));
  const clientChips = (() => {
    const m = new Map<string, number>();
    for (const r of clientWorkAll) {
      const n = (r.client_name || 'Unknown').trim();
      m.set(n, (m.get(n) || 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  })();
  const clientWorkRows = clientWorkAll.filter((r: any) => {
    const okClient = clientFilter === 'all' || (r.client_name || 'Unknown').trim() === clientFilter;
    const okQ = !bq || (r.name || '').toLowerCase().includes(bq.toLowerCase()) ||
      (r.client_name || '').toLowerCase().includes(bq.toLowerCase()) ||
      (r.expert?.name || '').toLowerCase().includes(bq.toLowerCase());
    return okClient && okQ;
  });
  const rosterLabel = (s?: string) => ({
    COMPLETED: 'Complete', LIVE: 'Live', IN_PROGRESS: 'Working', ASSIGNED: 'Assigned', PENDING: 'Queued',
  } as Record<string, string>)[(s || '').toUpperCase()] || (s || '—');

  // Cross into the builder planner in a NEW tab (pop-up-blocker-safe: the tab
  // opens inside the click, the crossing URL fills it once minted).
  const openPlanner = async (row: any) => {
    const tab = window.open('', '_blank');
    if (!tab) { toast.warn('Allow pop-ups for this site, then click the project again.'); return; }
    tab.document.write('<div style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font:14px system-ui;color:#6b7280">Opening the planner…</div>');
    setOpeningId(row.id);
    try {
      // request-agent wants the aibot client id (it resolves the founder from
      // `client`); workspace_id is the BUILDER workspace id — equal only for
      // aibot-provisioned workspaces, so prefer client_id when the row has it.
      const { url } = await requestAgentBridgeUrl(`/customer/agent/${row.id}`, row.client_id || row.workspace_id, row.id);
      if (url) tab.location.href = url;
      else { tab.close(); toast.error('Could not open the planner'); }
    } catch { tab.close(); toast.error('Could not open the planner'); }
    finally { setOpeningId(null); }
  };

  /*
   * Builds, drawn in the same kit as every other tab on this page.
   *
   * It used to render a page inside a tab: its own "Projects" heading, its own
   * sentence, its own pill filters and its own card language, so opening Builds
   * read as leaving Projects rather than moving along it. Everything it lists is
   * still exactly what it listed; the shell is the one the siblings use, which
   * is a Panel, a chip row over a count, and one ListRow per thing.
   *
   * The states people act on are chips now instead of coloured tiles, so a
   * queued build and a pending request say "Pending" the same way in both tabs.
   */
  let clientEmptyReason = 'A client project shows here as soon as the client starts one.';
  if (providerScope === 'expert') clientEmptyReason = 'When a project is assigned to you, it shows up here.';
  if (roster?.available === false) clientEmptyReason = 'Could not reach the Builder. Reload to retry.';

  const find = <SearchField value={bq} onChange={setBq} placeholder="Find a project" />;

  const scopeToggle = (
    <>
      {providerScope === 'owner' && lock === 'client' && !onlyClientId && clientChips.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 px-5 pb-4 md:px-6">
          <ClientPicker clients={clientChips} value={clientFilter} onChange={setClientFilter} total={clientWorkAll.length} />
        </div>
      )}
      {providerScope === 'owner' && !lock && (
        <div className="flex flex-wrap items-center gap-2 px-5 pb-4 md:px-6">
          <FilterChips
            tabs={[{ id: 'ours', label: 'Ours' }, { id: 'client', label: 'Client' }]}
            active={scope}
            onChange={(id) => { setScope(id as 'ours' | 'client'); setClientFilter('all'); }}
            label="Whose work"
          />
          {showClientWork && clientChips.length > 1 && (
            <ClientPicker clients={clientChips} value={clientFilter} onChange={setClientFilter} total={clientWorkAll.length} />
          )}
        </div>
      )}
      {providerScope === 'expert' && clientChips.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 px-5 pb-4 md:px-6">
          <ClientPicker clients={clientChips} value={clientFilter} onChange={setClientFilter} total={clientWorkAll.length} />
        </div>
      )}
    </>
  );

  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-6">
        <FilterChips
          tabs={[{ id: 'all', label: `All ${showClientWork ? clientWorkAll.length : projects.length}` }]}
          active="all"
          onChange={() => undefined}
          label="Build type"
        />
        {find}
      </div>
      {scopeToggle}

      {showClientWork && (
        <>
          {rosterLoading && <Loading label="Loading client work" />}
          {!rosterLoading && clientWorkRows.length === 0 && (
            <EmptyState
              title={providerScope === 'expert' ? 'Nothing assigned to you yet' : 'No client work here'}
              reason={clientEmptyReason}
            />
          )}
          {!rosterLoading && (
            <Divided>
              {clientWorkRows.map((row: any) => (
                <ListRow
                  key={row.id}
                  mark={<RouteMark kind="project" size="lg" />}
                  kicker={providerScope === 'expert' ? `For ${row.client_name || 'a client'}` : (row.client_name || 'Client')}
                  title={row.name}
                  badge={(
                    <>
                      <StatusChip status={rosterStatus(row.status)} label={rosterLabel(row.status)} />
                      {(row.needs_expert || 0) > 0 && <Chip tone="warn" dot>{row.needs_expert} waiting on expert</Chip>}
                    </>
                  )}
                  meta={[
                    { label: 'Expert', value: row.expert?.name || 'Not assigned yet' },
                    { label: 'Updated', value: timeAgo(row.updated_at || '') || 'Not recorded' },
                  ]}
                  right={(
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {canAssignExpert && providerScope === 'owner' && (
                        <AssignEngineer current={row.expert?.name} experts={experts} onPick={(value) => assignExpert(row.id, value)} />
                      )}
                      <Action size="sm" variant="quiet" arrow disabled={openingId === row.id} onClick={() => openPlanner(row)}>
                        {openingId === row.id ? 'Opening' : 'Open planner'}
                      </Action>
                    </div>
                  )}
                />
              ))}
            </Divided>
          )}
        </>
      )}

      {!showClientWork && (
        <>
          {loading && <Loading label="Loading projects" />}
          {!loading && filtered.length === 0 && (
            <EmptyState
              title="No projects yet"
              reason="A project shows here once its scoping is done and it is ready to build."
              action={(
                <Action onClick={() => window.dispatchEvent(new CustomEvent('orbit:open', { detail: { route: 'problem', fresh: true, context: 'problem' } }))}>
                  Scope a problem
                </Action>
              )}
            />
          )}
          {!loading && (
            <Divided>
              {filtered.map((v) => {
                const d = describeBuild(v);
                const handled = v.completedTickets ?? v.ticketCount ?? 0;
                const solves = (v.understandingSummary || v.customerGoal || v.description || '').trim();
                const owner = v.ownerMember?.email ? v.ownerMember.email.split('@')[0] : 'you';
                const when = timeAgo(v.updatedAt || v.completedAt || v.createdAt);
                // Progress is shown ONLY when the data actually carries it: the
                // build's own progress, or a real ticket ratio. Never a default.
                const progress = typeof v.latestDisplay?.progress === 'number'
                  ? Math.max(0, Math.min(100, v.latestDisplay.progress))
                  : (v.ticketCount ? Math.round(((v.completedTickets || 0) / v.ticketCount) * 100) : null);
                const meta = [] as Array<{ label: string; value: React.ReactNode; foot?: string }>;
                if (d.mode === 'building' && progress != null) meta.push({ label: 'Progress', value: `${progress}%` });
                if (handled > 0 && meta.length === 0) meta.push({ label: 'Handled', value: `${handled.toLocaleString()} ${handled === 1 ? 'ticket' : 'tickets'}` });
                if (meta.length === 0) meta.push({ label: '', value: '' });
                meta.push({ label: 'Updated', value: when || 'Not recorded', foot: `by ${owner}` });
                return (
                  <ListRow
                    key={v.id}
                    mark={<RouteMark kind="project" size="lg" />}
                    kicker="Project"
                    title={v.title}
                    badge={<StatusChip status={buildStatus(d.mode)} label={d.status} />}
                    desc={solves || undefined}
                    foot={expertLine(v)}
                    meta={meta}
                    metaWidth={OWN_COLUMNS}
                    dense
                    rightWidth={OWN_RIGHT}
                    right={(
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {canAssignExpert && (
                          <AssignEngineer current={v.expert?.name} experts={experts} onPick={(value) => assignExpert(v.id, value)} />
                        )}
                        <Action
                          size="sm"
                          variant="quiet"
                          arrow={d.openable}
                          disabled={!d.openable}
                          reason="This build was cancelled, so there is nothing behind it to open"
                          onClick={() => openBuild(v)}
                          className="h-[28px] w-[148px]"
                        >
                          {d.openable ? 'Open workspace' : 'Cancelled'}
                        </Action>
                        <Action size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setDeleteTarget(v)} className="h-[28px] w-[28px] px-0">
                          <span className="sr-only">Delete</span>
                        </Action>
                      </div>
                    )}
                  />
                );
              })}
            </Divided>
          )}
        </>
      )}

      <DeleteAgentDialog
        project={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDone={() => { setDeleteTarget(null); fetchAgents(); }}
      />
    </Panel>
  );
};

/**
 * Two-stage delete confirmation:
 *
 *   Stage 1 — first click of the modal's Delete button calls the builder
 *   without `force`. If the agent has no committed work (the ghost case),
 *   the builder deletes immediately and we close.
 *
 *   Stage 2 — if the builder responds with HAS_COMMITTED_WORK + counts,
 *   we re-render the modal showing what would be lost (PRDs, tickets,
 *   activity, artifacts) and ask the user to confirm a hard delete. The
 *   second confirm calls with `force=true`.
 *
 * This makes the common "clean up an empty agent I created by mistake"
 * path one click + one confirm, while still protecting projects with
 * real work.
 */
const DeleteAgentDialog: React.FC<{
  project: Project | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ project, onClose, onDone }) => {
  const [deleting, setDeleting] = useState(false);
  const [committedCounts, setCommittedCounts] = useState<{
    prds: number;
    nonPendingTickets: number;
    activityEvents: number;
    artifacts: number;
  } | null>(null);

  // Reset committed-counts each time the modal opens for a new target.
  useEffect(() => {
    if (project) setCommittedCounts(null);
  }, [project?.id]);

  if (!project) return null;

  const requireForce = !!committedCounts;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await agentsApi.deleteProject(project.id, requireForce);
      toast.success(`Deleted "${project.title}"`);
      onDone();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.error === 'HAS_COMMITTED_WORK' && detail?.counts) {
        // Stage 2 — surface what would be deleted, ask to confirm hard delete.
        setCommittedCounts(detail.counts);
      } else {
        console.error('[DeleteAgentDialog] delete failed', err);
        toast.error(detail?.error || detail?.message || 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">
              {requireForce ? 'This agent has work — delete anyway?' : 'Delete this agent?'}
            </h3>
            <p className="text-sm text-gray-600 mt-1.5">
              <span className="font-medium text-gray-900">{project.title}</span>
              {requireForce ? ' contains real work that will be permanently lost:' : ' will be removed from your workspace.'}
            </p>
            {requireForce && committedCounts && (
              <ul className="mt-3 space-y-1 text-xs text-gray-600">
                {committedCounts.prds > 0 && <li>· {committedCounts.prds} PRD{committedCounts.prds === 1 ? '' : 's'}</li>}
                {committedCounts.nonPendingTickets > 0 && <li>· {committedCounts.nonPendingTickets} ticket{committedCounts.nonPendingTickets === 1 ? '' : 's'} with status changes</li>}
                {committedCounts.activityEvents > 0 && <li>· {committedCounts.activityEvents} executor activity events</li>}
                {committedCounts.artifacts > 0 && <li>· {committedCounts.artifacts} artifact{committedCounts.artifacts === 1 ? '' : 's'} (memos / proposals / mockups)</li>}
              </ul>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
          >
            {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <Trash2 className="w-3.5 h-3.5" />
            {requireForce ? 'Delete anyway' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Role router for /projects — ONE page, one design (CTO 08-18). Providers get
// the same projects page with the Ours | Client work scope inside it (client
// chips in Client work); experts get client work only; customers see their
// own projects exactly as before. Flags come from /aai/me via useParticipant
// (reactive), with a localStorage fallback for instant paint.
const ClientAgentsPage: React.FC = () => {
  const part: any = useParticipant();
  const ctx: any = part?.context;
  const isProvider = ctx?.is_provider ?? (typeof localStorage !== 'undefined' && localStorage.getItem('is_provider') === '1');
  const isExpert = ctx?.is_expert ?? (typeof localStorage !== 'undefined' && localStorage.getItem('is_expert') === '1');
  const isProviderOwner = ctx?.is_provider_owner ?? (typeof localStorage !== 'undefined' && localStorage.getItem('is_provider_owner') === '1');
  const isBuilderAdmin = ctx?.is_builder_admin ?? (typeof localStorage !== 'undefined' && localStorage.getItem('is_builder_admin') === '1');
  // CTO 08-20: a PURE expert (no owner standing, no builder-admin flag) sees
  // only the projects they are assigned to. Every expert also sits on the
  // provider roster, so is_expert implies is_provider — the expert check must
  // come first or it is unreachable. Owners who also carry expert assignments
  // (the default-expert case) keep the full lens.
  if (isExpert && !isProviderOwner && !isBuilderAdmin) return <CustomerProjectsView providerScope="expert" />;
  // Only the provider's Owner (or founder) gets the Ours | Client lens; the
  // roster API answers anyone else on the provider with a 403, so a Team Lead
  // or participant keeps the customer view instead of a toggle that fails.
  if (isProvider && (isProviderOwner || isBuilderAdmin)) return <CustomerProjectsView providerScope="owner" />;
  return <CustomerProjectsView />;
};

/* The /projects page for everyone without a dedicated projects page of their
   own. The list above is also embedded in other pages, so the heading lives
   here and not in it. */
export const ProjectsScreen: React.FC = () => {
  const isExpert = localStorage.getItem('is_expert') === '1';
  const ownerLens = localStorage.getItem('is_provider_owner') === '1' || localStorage.getItem('is_builder_admin') === '1';
  const clientWorkOnly = isExpert && !ownerLens;
  return (
    <PageBody>
      <PageHead
        title={clientWorkOnly ? 'Client work' : 'Company projects'}
        sub={clientWorkOnly ? 'The client projects assigned to you.' : 'Everything your company is building, from scoping to live.'}
      />
      <ClientAgentsPage />
    </PageBody>
  );
};

export default ClientAgentsPage;
